import type { CSSProperties, FC } from 'react'

import { useState } from 'react'
import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatNumber, formatUsd } from '@/lib/format'
import ButtonLink from '@/components/landing/button-link'
import CountUp from '@/components/landing/count-up'
import SectionHeading, { Accent } from '@/components/landing/section-heading'
import { LINKS, PRICING_NOTES, PRICING_TIERS } from '@/constants/kinematic'

const LAST = PRICING_TIERS.length - 1

const PricingSection: FC = () => {
    const [tier, setTier] = useState(2)
    const current = PRICING_TIERS[tier]

    const sliderStyle = {
        '--range-progress': `${(tier / LAST) * 100}%`,
        '--range-rest': 'rgba(255, 255, 255, 0.14)'
    } as CSSProperties

    return (
        <section id='pricing' aria-labelledby='pricing-title' className='relative bg-bone py-24 text-ink md:py-32'>
            <div className='mx-auto max-w-[1320px] px-5 md:px-8'>
                <SectionHeading
                    id='pricing-title'
                    tone='light'
                    index='06'
                    label='Pricing'
                    title={<>A flat fee by tier. <Accent>No revenue share.</Accent> No cap on bets.</>}
                    lead='Your tier follows the monthly deposits you declare at sign-up and at each renewal. You pay each quarter upfront in USDC, USDT or SOL.'
                />

                <div className='mt-14 grid gap-4 lg:grid-cols-12'>
                    <div className='relative overflow-hidden rounded-[32px] bg-ink p-7 text-bone md:p-10 lg:col-span-7'>
                        <div aria-hidden='true' className='pointer-events-none absolute -right-24 -top-24 size-[420px] rounded-full bg-ember/20 blur-[100px]' />
                        <div aria-hidden='true' className='grid-lines pointer-events-none absolute inset-0 opacity-60 [mask-image:linear-gradient(to_bottom,black,transparent_70%)]' />

                        <div className='relative'>
                            <div className='flex flex-wrap items-center justify-between gap-3'>
                                <span className='font-mono text-[10px] uppercase tracking-[0.18em] text-steel'>Declared monthly deposits</span>
                                <span className='rounded-full border border-white/10 px-3 py-1 font-mono text-[11px] text-bone'>{current.label}</span>
                            </div>

                            <input
                                type='range'
                                min={0}
                                max={LAST}
                                step={1}
                                value={tier}
                                onChange={event => setTier(Number(event.target.value))}
                                aria-label='Declared monthly deposits'
                                aria-valuetext={current.label}
                                className='range-ember mt-8 w-full'
                                style={sliderStyle}
                            />

                            <div className='mt-2 flex justify-between font-mono text-[10px] text-steel'>
                                {PRICING_TIERS.map((item, index) => (
                                    <button
                                        key={item.short}
                                        type='button'
                                        onClick={() => setTier(index)}
                                        className={cn('transition-colors hover:text-bone', index === tier && 'text-ember')}
                                    >
                                        {item.short}
                                    </button>
                                ))}
                            </div>

                            <div className='mt-12 flex items-end gap-3'>
                                <span className='flex items-start text-[clamp(4.5rem,11vw,8.5rem)] font-semibold leading-[0.8] tracking-[-0.045em] stretch-condensed'>
                                    <span className='mr-1 mt-[0.08em] text-[0.4em] text-steel'>$</span>
                                    <CountUp value={current.monthly} duration={600} format={value => formatNumber(Math.round(value))} />
                                </span>
                                <span className='pb-2 font-mono text-xs text-steel'>per month</span>
                            </div>

                            <p className='mt-5 text-sm text-steel'>
                                Billed <span className='text-bone'>{formatUsd(current.quarterly)}</span> each quarter, upfront, through Solana Pay.
                            </p>

                            <ul className='mt-8 grid gap-3 border-t border-white/10 pt-8 sm:grid-cols-2'>
                                {PRICING_NOTES.map(note => (
                                    <li key={note} className='flex gap-2.5 text-sm leading-relaxed text-bone/85'>
                                        <Check aria-hidden='true' className='mt-0.5 size-4 shrink-0 text-ember' />
                                        {note}
                                    </li>
                                ))}
                            </ul>

                            <div className='mt-10 flex flex-wrap gap-3'>
                                <ButtonLink href={LINKS.signUp} external>
                                    Start free in sandbox
                                </ButtonLink>
                                <ButtonLink href={LINKS.email} variant='ghost'>
                                    Talk to us
                                </ButtonLink>
                            </div>
                        </div>
                    </div>

                    <div className='rounded-[32px] border border-black/[0.06] bg-white p-2 lg:col-span-5'>
                        <table className='w-full border-separate border-spacing-0 text-left'>
                            <caption className='sr-only'>Kinematic fees by declared monthly deposits</caption>
                            <thead>
                                <tr className='font-mono text-[10px] uppercase tracking-[0.14em] text-ink/70'>
                                    <th scope='col' className='px-4 py-4 font-normal'>Monthly deposits</th>
                                    <th scope='col' className='px-4 py-4 text-right font-normal'>Per month</th>
                                    <th scope='col' className='hidden px-4 py-4 text-right font-normal sm:table-cell'>Per quarter</th>
                                </tr>
                            </thead>
                            <tbody>
                                {PRICING_TIERS.map((item, index) => {
                                    const selected = index === tier
                                    const cell = cn('py-3.5 transition-colors', selected ? 'bg-ink text-bone' : 'text-ink group-hover:bg-black/[0.03]')

                                    return (
                                        <tr key={item.label} className='group'>
                                            <th scope='row' className={cn(cell, 'rounded-l-2xl px-4 text-sm font-normal')}>
                                                <button
                                                    type='button'
                                                    onClick={() => setTier(index)}
                                                    aria-pressed={selected}
                                                    className='flex w-full items-center gap-2.5 text-left'
                                                >
                                                    <span className={cn('size-1.5 rounded-full', selected ? 'bg-ember' : 'bg-ink/20')} />
                                                    {item.label}
                                                </button>
                                            </th>
                                            <td className={cn(cell, 'px-4 text-right font-mono text-sm tabular-nums max-sm:rounded-r-2xl')}>
                                                {formatUsd(item.monthly)}
                                            </td>
                                            <td className={cn(cell, 'hidden rounded-r-2xl px-4 text-right font-mono text-sm tabular-nums sm:table-cell', selected ? 'text-bone/70' : 'text-ink/70')}>
                                                {formatUsd(item.quarterly)}
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>

                        <p className='px-4 pb-4 pt-5 text-xs leading-relaxed text-ink/70'>
                            The fee is the same whatever your players bet or win. Months of a paid quarter that have not started are refunded.
                        </p>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default PricingSection
